import { useEffect, useMemo, useState } from 'react';
import {
  DAMAGE_GROUPS,
  DamageLocation,
  ROAD_DAMAGES,
  RoadDamageType,
  severityFrom,
} from '../../damages/roadDamageCatalog';
import { translateRoadCatalog } from '../../damages/roadDamageTranslations';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@mui/icons-material/Download';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { CapturedLocation, RoadDamageCamera } from './RoadDamageCamera';
import { RoadDamageVideo } from './RoadDamageVideo';
import { RoadDamagesMap } from './RoadDamagesMap';
import { workOrderForRecord } from '../../damages/roadWorkOrder';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import './RoadDamagesPanel.css';

type Severity = 'N1' | 'N2' | 'N3' | 'N4';

type RoadDamageRecord = {
  id: number;
  roadDamageNumber: number;
  roadDamageType: string;
  measurementUnit: string | null;
  dimension: number | null;
  roadElement: string;
  damageCategory: number;
  intensity: number;
  severity: Severity;
  operatorComment?: string | null;
  roadWorkOrder?: string | null;
  roadHasVideo?: boolean | null;
  roadVideoUrl?: string | null;
  roadImageData: string;
  roadLatitude: number | null;
  roadLongitude: number | null;
  locationAccuracy?: number | null;
  roadRemediated?: boolean | null;
  roadRemediatedAt?: string | null;
  roadRemediatedImage?: string | null;
  roadInspector?: string | null;
  roadInspectorUserId?: number | null;
  roadRemedyPending?: boolean | null;
  roadRemedySuggestionImage?: string | null;
  roadRemedySuggestedBy?: string | null;
  roadRemedySuggestionAt?: string | null;
  roadDeletePending?: boolean | null;
  roadDeleteRequestedBy?: string | null;
  roadDeleteRequestedAt?: string | null;
  registeredAt: string;
  createdAt: string;
};

const GROUPED = ['Deformaciones', 'Roturas', 'Exudaciones'];
const SELECTABLE_DAMAGES = ROAD_DAMAGES.filter(
  (damage) => damage.locations.length > 0 && damage.name.trim() !== 'Rotura'
);

const locationFamilyOf = (location: DamageLocation, all: DamageLocation[]) => {
  if (location.family) return location.family;
  const sibling = all.find((item) => item.parent === location.parent && item.family);
  return sibling?.family || location.parent || '';
};

const backendUrl = () => sessionStorage.getItem('backendUrl') || 'http://localhost:1337';

// En desarrollo el frontend proxea /detector al servicio Python (setupProxy.js).
// Detrás de nginx la misma ruta no existe: se usa /api, que nginx envía a Python.
const detectorUrl = () =>
  sessionStorage.getItem('detectorUrl') ||
  (window.location.port === '9002' ? '/detector' : '/api');

type Detection = {
  classId: number;
  className: string;
  damageName: string;
  confidence: number;
  box: { x: number; y: number; w: number; h: number };
};

function severityRank(value?: Severity | null): number {
  return value ? Number(value.slice(1)) : 0;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fileNameFromHeader(response: Response, fallback: string) {
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  return match?.[1] || fallback;
}

function fileToJpeg(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('file'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('image'));
      img.onload = () => {
        const max = 1280;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const context = canvas.getContext('2d');
        if (!context) {
          reject(new Error('canvas'));
          return;
        }
        context.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      img.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
  });
}

function safeProjectFileName(name?: string) {
  return (
    (name || 'proyecto')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || 'proyecto'
  );
}

async function downloadPdf(url: string, fallbackName: string, token: string | null, errorMessage?: string) {
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new Error(errorMessage || 'PDF');
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileNameFromHeader(response, fallbackName);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

type Props = { projectId: number; projectName?: string };

export const RoadDamagesStrapiPanel = ({ projectId, projectName }: Props) => {
  const { user, token } = useAuth();
  const { t, i18n } = useTranslation();
  const trCatalog = (value?: string | null) => translateRoadCatalog(i18n.language, value);
  const canCreate = ['admin', 'project_manager', 'worker'].includes(user?.role || '');
  const canDelete = ['admin', 'project_manager'].includes(user?.role || '');
  const canSuggest = user?.role === 'worker';
  const [records, setRecords] = useState<RoadDamageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [remedyRecord, setRemedyRecord] = useState<RoadDamageRecord | null>(null);
  const [remedyMode, setRemedyMode] = useState<'direct' | 'suggest'>('direct');
  const [reviewRecord, setReviewRecord] = useState<RoadDamageRecord | null>(null);
  const [deleteReview, setDeleteReview] = useState<RoadDamageRecord | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [remedyImage, setRemedyImage] = useState('');
  const [remedyCamera, setRemedyCamera] = useState(false);
  const [remedying, setRemedying] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [imageName, setImageName] = useState('');
  const [capturedLocation, setCapturedLocation] = useState<CapturedLocation | null>(null);
  const [groupChoice, setGroupChoice] = useState('');
  const [damageId, setDamageId] = useState('');
  const [dimension, setDimension] = useState('');
  const [locationId, setLocationId] = useState('');
  const [elementFamily, setElementFamily] = useState('');
  const [intensity, setIntensity] = useState<number | ''>('');
  const [comment, setComment] = useState('');
  const [videoDataUrl, setVideoDataUrl] = useState('');
  const [playingVideo, setPlayingVideo] = useState<{ id: number; url: string } | null>(null);
  const [error, setError] = useState('');
  const [openedAt, setOpenedAt] = useState(new Date().toISOString());
  // 'image': solo se pide la foto. 'review': resultado del detector. 'form': ficha.
  const [step, setStep] = useState<'image' | 'review' | 'form'>('image');
  const [detecting, setDetecting] = useState(false);
  const [detection, setDetection] = useState<Detection | null>(null);
  const [noDetection, setNoDetection] = useState(false);
  const [lockedDamage, setLockedDamage] = useState(false);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl()}/api/road-damages/project/${projectId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(t('roadDamages.loadError'));
      const payload = await response.json();
      setRecords(payload.data || []);
    } catch (err) {
      console.error(err);
      setError(t('roadDamages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);

  const singles = SELECTABLE_DAMAGES.filter((damage) => !GROUPED.includes(damage.group));
  const selected: RoadDamageType | undefined = SELECTABLE_DAMAGES.find(
    (damage) => damage.id === damageId
  );

  const locationsByFamily = useMemo(() => {
    const map = new Map<string, RoadDamageType['locations']>();
    const all = selected?.locations || [];
    all.forEach((location) => {
      const key = locationFamilyOf(location, all) || t('roadDamages.element');
      map.set(key, [...(map.get(key) || []), location]);
    });
    return map;
  }, [selected, t]);

  const elementFamilies = useMemo(() => Array.from(locationsByFamily.keys()), [locationsByFamily]);
  const activeFamily = elementFamilies.length === 1 ? elementFamilies[0] : elementFamily;
  const familyLocations = locationsByFamily.get(activeFamily) || [];

  const selectedLocation = selected?.locations.find((location) => location.id === locationId);
  const category = selectedLocation?.category ?? null;
  const severity: Severity | null =
    category && intensity ? severityFrom(category, Number(intensity)) : null;
  const maxSeverity = records.reduce<Severity | null>((max, record) => {
    return severityRank(record.severity) > severityRank(max) ? record.severity : max;
  }, null);

  const resetForm = () => {
    setImageDataUrl('');
    setImageName('');
    setCapturedLocation(null);
    setGroupChoice('');
    setDamageId('');
    setDimension('');
    setLocationId('');
    setElementFamily('');
    setIntensity('');
    setComment('');
    setVideoDataUrl('');
    setError('');
    setStep('image');
    setDetection(null);
    setNoDetection(false);
    setLockedDamage(false);
  };

  const openForm = () => {
    resetForm();
    setOpenedAt(new Date().toISOString());
    setOpen(true);
  };

  // Fija el tipo de daño que propone el detector y bloquea los selectores.
  const applyDetection = (found: Detection) => {
    const match = SELECTABLE_DAMAGES.find((damage) => damage.name === found.damageName);
    if (!match) return false;
    setGroupChoice(`group:${match.group}`);
    setDamageId(match.id);
    setLockedDamage(true);
    return true;
  };

  const analyze = async () => {
    if (!imageDataUrl) return setError(t('roadDamages.imageRequired'));
    setDetecting(true);
    setError('');
    setDetection(null);
    setNoDetection(false);
    try {
      const response = await fetch(`${detectorUrl()}/detect-road-damage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: imageDataUrl }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || t('roadDamages.detectorUnavailable'));
      if (payload?.detection) setDetection(payload.detection);
      else setNoDetection(true);
      setStep('review');
    } catch (err) {
      setError(
        err instanceof Error
          ? `${err.message}. ${t('roadDamages.continueManual')}`
          : t('roadDamages.detectorUnavailable')
      );
    } finally {
      setDetecting(false);
    }
  };

  const acceptDetection = () => {
    if (detection && applyDetection(detection)) {
      setStep('form');
      setError('');
    } else {
      setError(t('roadDamages.catalogMismatch'));
      setLockedDamage(false);
      setStep('form');
    }
  };

  const rejectDetection = () => {
    setGroupChoice('');
    setDamageId('');
    setLockedDamage(false);
    setStep('form');
    setError('');
  };

  const onCameraCapture = (image: string, location: CapturedLocation | null) => {
    setImageDataUrl(image);
    setImageName(t('roadDamages.capturedPhoto'));
    setCapturedLocation(location);
    setError('');
  };

  const resetCameraCapture = () => {
    setImageDataUrl('');
    setImageName('');
    setCapturedLocation(null);
    setDetection(null);
    setNoDetection(false);
    setError('');
  };

  const onGroupChange = (value: string) => {
    setGroupChoice(value);
    setDamageId(value.startsWith('damage:') ? value.slice('damage:'.length) : '');
    setLocationId('');
    setElementFamily('');
    setDimension('');
  };

  const submit = async () => {
    if (!imageDataUrl) return setError(t('roadDamages.imageRequired'));
    if (!selected) return setError(t('roadDamages.typeRequired'));
    if (selected.unit && (!dimension.trim() || Number(dimension) <= 0)) {
      return setError(t('roadDamages.dimensionRequired', { unit: selected.unit }));
    }
    if (!selectedLocation) return setError(t('roadDamages.elementRequired'));
    if (!intensity) return setError(t('roadDamages.intensityRequired'));

    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${backendUrl()}/api/road-damages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          projectId,
          damageType: selected.name,
          measurementUnit: selected.unit,
          dimension: selected.unit ? Number(dimension) : null,
          element: selectedLocation.label,
          category,
          intensity: Number(intensity),
          severity,
          comment: comment.trim() || null,
          imageDataUrl,
          videoDataUrl: videoDataUrl || null,
          latitude: capturedLocation?.latitude ?? null,
          longitude: capturedLocation?.longitude ?? null,
          locationAccuracy: capturedLocation?.accuracy ?? null,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message || t('roadDamages.saveError'));
      }
      await fetchRecords();
      setOpen(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roadDamages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const closeVideo = () => {
    if (playingVideo?.url.startsWith('blob:')) URL.revokeObjectURL(playingVideo.url);
    setPlayingVideo(null);
  };

  const playVideo = async (record: RoadDamageRecord) => {
    setError('');
    if (record.roadVideoUrl) {
      setPlayingVideo({ id: record.id, url: `${backendUrl()}${record.roadVideoUrl}` });
      return;
    }
    try {
      const response = await fetch(`${backendUrl()}/api/road-damages/${record.id}/video`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(t('roadDamages.videoError'));
      const buffer = await response.arrayBuffer();
      if (buffer.byteLength < 32) throw new Error(t('roadDamages.videoError'));
      const mime = (response.headers.get('content-type') || 'video/webm').split(';')[0] || 'video/webm';
      setPlayingVideo({
        id: record.id,
        url: URL.createObjectURL(new Blob([buffer], { type: mime.startsWith('video/') ? mime : 'video/webm' })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roadDamages.videoError'));
    }
  };

  const remove = async (record: RoadDamageRecord) => {
    if (!window.confirm(t('roadDamages.confirmDelete', {
      id: String(record.roadDamageNumber).padStart(4, '0'),
    }))) {
      return;
    }
    const response = await fetch(`${backendUrl()}/api/road-damages/${record.id}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.ok) await fetchRecords();
    else setError(t('roadDamages.deleteError'));
  };

  const closeRemedy = () => {
    if (remedying) return;
    setRemedyRecord(null);
    setRemedyImage('');
    setRemedyCamera(false);
  };

  const submitRemedy = async () => {
    if (!remedyRecord || !remedyImage) {
      setError(t('roadDamages.remedyRequired'));
      return;
    }
    setRemedying(true);
    setError('');
    try {
      const response = await fetch(
        `${backendUrl()}/api/road-damages/${remedyRecord.id}/${
          remedyMode === 'suggest' ? 'suggest-remedy' : 'remediate'
        }`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ imageDataUrl: remedyImage }),
        }
      );
      if (!response.ok) throw new Error(t('roadDamages.remedyError'));
      await fetchRecords();
      setRemedyRecord(null);
      setRemedyImage('');
      setRemedyCamera(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roadDamages.remedyError'));
    } finally {
      setRemedying(false);
    }
  };

  const workerOwns = (record: RoadDamageRecord) => {
    if (user?.role !== 'worker') return false;
    if (record.roadInspectorUserId != null && Number(record.roadInspectorUserId) === Number(user.id)) {
      return true;
    }
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return Boolean(
      (name && record.roadInspector === name) ||
      (user.username && record.roadInspector === user.username)
    );
  };

  const requestDeletion = async (record: RoadDamageRecord) => {
    setError('');
    try {
      const response = await fetch(`${backendUrl()}/api/road-damages/${record.id}/request-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error(t('roadDamages.requestDeleteError'));
      await fetchRecords();
    } catch {
      setError(t('roadDamages.requestDeleteError'));
    }
  };

  const reviewDeletion = async (decision: 'accept' | 'reject') => {
    if (!deleteReview) return;
    setReviewing(true);
    setError('');
    try {
      const response = await fetch(`${backendUrl()}/api/road-damages/${deleteReview.id}/review-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ decision }),
      });
      if (!response.ok) throw new Error(t('roadDamages.deleteError'));
      await fetchRecords();
      setDeleteReview(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roadDamages.deleteError'));
    } finally {
      setReviewing(false);
    }
  };

  const openRemedy = (record: RoadDamageRecord, mode: 'direct' | 'suggest') => {
    setRemedyRecord(record);
    setRemedyMode(mode);
    setRemedyImage('');
    setRemedyCamera(false);
    setError('');
  };

  const reviewSuggestion = async (decision: 'accept' | 'reject') => {
    if (!reviewRecord) return;
    setReviewing(true);
    setError('');
    try {
      const response = await fetch(`${backendUrl()}/api/road-damages/${reviewRecord.id}/review-remedy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ decision }),
      });
      if (!response.ok) throw new Error(t('roadDamages.remedyError'));
      await fetchRecords();
      setReviewRecord(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('roadDamages.remedyError'));
    } finally {
      setReviewing(false);
    }
  };

  const downloadList = (format: 'pdf' | 'xlsx') => {
    const base = safeProjectFileName(projectName);
    const lang = (i18n.language || 'es').split('-')[0];
    const prefix =
      lang === 'en' ? 'damage_list' : lang === 'eu' ? 'kalteen_zerrenda' : 'listado_danos';
    setExporting(true);
    setExportOpen(false);
    downloadPdf(
      `${backendUrl()}/api/road-damages/project/${projectId}/summary-${format}?lang=${encodeURIComponent(lang)}`,
      `${prefix}_${base}.${format}`,
      token
    )
      .catch(() => setError(t('roadDamages.listError')))
      .finally(() => setExporting(false));
  };

  return (
    <div className="road-damages">
      <div className="road-damages-head">
        <div className="road-head-top">
          <section className="road-summary-block">
            <h3>{t('roadDamages.summary')}</h3>
            <div className="road-summary">
              <div className="road-summary-card">
                <strong>{records.length}</strong>
                <span>{t('roadDamages.count')}</span>
              </div>
              <div className="road-summary-card">
                <strong className={maxSeverity ? `sev sev-${maxSeverity}` : ''}>
                  {maxSeverity || '—'}
                </strong>
                <span>{t('roadDamages.maxSeverity')}</span>
              </div>
            </div>
          </section>
          {canCreate && (
            <button type="button" className="attach-damage-btn" onClick={openForm}>
              {t('roadDamages.attach')}
            </button>
          )}
        </div>
        <div className="road-head-actions">
          <button
            type="button"
            className="road-download-btn"
            disabled={exporting}
            onClick={() => setExportOpen(true)}
          >
            <DownloadIcon fontSize="small" />
            {t('roadDamages.downloadList')}
          </button>
          <button type="button" className="road-map-btn" onClick={() => setShowMap(true)}>
            <MapOutlinedIcon fontSize="small" />
            {t('roadDamages.viewMap')}
          </button>
        </div>
      </div>

      {error && !open && <p className="road-error">{error}</p>}
      {loading ? (
        <p className="road-empty">{t('roadDamages.loading')}</p>
      ) : records.length === 0 ? (
        <p className="road-empty">{t('roadDamages.empty')}</p>
      ) : (
        <div className="road-cards">
          {records.map((record) => (
            <article key={record.id} className="road-card">
              <img className="road-card-photo" src={record.roadImageData} alt={trCatalog(record.roadDamageType)} />
              <div className="road-card-body">
                <div className="road-card-top">
                  <h4>ID:{String(record.roadDamageNumber).padStart(4, '0')}</h4>
                  <span className={`sev sev-${record.severity}`}>{record.severity}</span>
                  {record.roadHasVideo && (
                    <button
                      type="button"
                      className="road-video-icon"
                      title={t('roadDamages.playVideo')}
                      onClick={() => playVideo(record)}
                    >
                      <VideocamOutlinedIcon fontSize="small" />
                    </button>
                  )}
                </div>
                <p className="road-card-title">{trCatalog(record.roadDamageType)}</p>
                <p>{t('roadDamages.element')}: {trCatalog(record.roadElement)}</p>
                {record.measurementUnit && (
                  <p>
                    {t('roadDamages.dimension')}: {record.dimension} {record.measurementUnit}
                  </p>
                )}
                <p>
                  {t('roadDamages.intensity')}: {record.intensity} · {t('roadDamages.category')}{' '}
                  {record.damageCategory}
                </p>
                {record.roadLatitude != null && record.roadLongitude != null && (
                  <p>
                    {t('roadDamages.location')}: {Number(record.roadLatitude).toFixed(6)},{' '}
                    {Number(record.roadLongitude).toFixed(6)}
                  </p>
                )}
                <p>
                  {t('roadDamages.inspectedBy')}: {record.roadInspector || '—'}
                </p>
                <p className="road-card-date">
                  {formatDate(record.registeredAt || record.createdAt)}
                </p>
                {record.operatorComment && <p>{t('roadDamages.comment')}: {record.operatorComment}</p>}
                {!record.roadRemediated && workOrderForRecord(record) && (
                  <p className="road-work-order">
                    {t('roadDamages.workOrder')}: {workOrderForRecord(record)}
                  </p>
                )}
                <p>
                  {t('roadDamages.remediated')}:{' '}
                  {record.roadRemediated ? t('roadDamages.remediatedYes') : t('roadDamages.remediatedNo')}
                  {record.roadRemediated && record.roadRemediatedAt
                    ? ` · ${formatDate(record.roadRemediatedAt)}`
                    : ''}
                </p>
                {canDelete && (
                  <div className="road-card-actions">
                    <button type="button" className="road-delete-btn" onClick={() => remove(record)}>
                      {t('roadDamages.delete')}
                    </button>
                  </div>
                )}
                {record.roadDeletePending && (canDelete || workerOwns(record)) && (
                  <div className="road-delete-pending">
                    <span>
                      <HourglassTopIcon fontSize="small" />
                      {canDelete
                        ? t('roadDamages.deleteRequestPending')
                        : t('roadDamages.waitingDelete')}
                    </span>
                    {canDelete && (
                      <button
                        type="button"
                        className="road-review-btn"
                        onClick={() => {
                          setDeleteReview(record);
                          setError('');
                        }}
                      >
                        {t('roadDamages.reviewDelete')}
                      </button>
                    )}
                  </div>
                )}
                {canSuggest && workerOwns(record) && !record.roadDeletePending && (
                  <div className="road-card-actions">
                    <button
                      type="button"
                      className="road-delete-btn"
                      onClick={() => requestDeletion(record)}
                    >
                      {t('roadDamages.requestDelete')}
                    </button>
                  </div>
                )}
              </div>
              <aside className={`road-card-status ${
                record.roadRemediated
                  ? 'is-ok'
                  : record.roadRemedyPending && (canDelete || canSuggest)
                    ? 'is-pending'
                    : 'is-open'
              }`}>
                {record.roadRemediated ? (
                  <span className="road-status-label">
                    <CheckCircleIcon fontSize="small" />
                    {t('roadDamages.damageFixed')}
                  </span>
                ) : record.roadRemedyPending && canDelete ? (
                  <>
                    <span className="road-status-label">
                      <HourglassTopIcon fontSize="small" />
                      {t('roadDamages.suggestionPending')}
                    </span>
                    <button
                      type="button"
                      className="road-review-btn"
                      onClick={() => {
                        setReviewRecord(record);
                        setError('');
                      }}
                    >
                      {t('roadDamages.reviewSuggestion')}
                    </button>
                    <button
                      type="button"
                      className="road-remedy-btn"
                      onClick={() => openRemedy(record, 'direct')}
                    >
                      {t('roadDamages.markRemediated')}
                    </button>
                  </>
                ) : record.roadRemedyPending && canSuggest ? (
                  <span className="road-status-label">
                    <HourglassTopIcon fontSize="small" />
                    {t('roadDamages.waitingManager')}
                  </span>
                ) : (
                  <>
                    <span className="road-status-label">
                      <CancelIcon fontSize="small" />
                      {t('roadDamages.damageOpen')}
                    </span>
                    {canDelete && (
                      <button
                        type="button"
                        className="road-remedy-btn"
                        onClick={() => openRemedy(record, 'direct')}
                      >
                        {t('roadDamages.markRemediated')}
                      </button>
                    )}
                    {canSuggest && (
                      <button
                        type="button"
                        className="road-review-btn"
                        onClick={() => openRemedy(record, 'suggest')}
                      >
                        {t('roadDamages.suggestRemediated')}
                      </button>
                    )}
                  </>
                )}
              </aside>
            </article>
          ))}
        </div>
      )}

      {open && (
        <div className="road-modal-backdrop" onClick={() => !saving && setOpen(false)}>
          <div className="road-modal" onClick={(event) => event.stopPropagation()}>
            <div className="road-modal-head">
              <h3>{t('roadDamages.title')}</h3>
              <button
                type="button"
                className="road-close"
                disabled={saving}
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>

            {step === 'image' && (
              <>
                <RoadDamageCamera
                  onCapture={onCameraCapture}
                  onReset={resetCameraCapture}
                />
                {capturedLocation && (
                  <p className="road-note">
                    {t('roadDamages.location')}: {capturedLocation.latitude.toFixed(6)},{' '}
                    {capturedLocation.longitude.toFixed(6)}
                  </p>
                )}
                {error && <p className="road-error">{error}</p>}
                <div className="road-actions">
                  <button
                    type="button"
                    className="road-cancel"
                    disabled={detecting}
                    onClick={() => setOpen(false)}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    className="attach-damage-btn"
                    disabled={detecting || !imageDataUrl}
                    onClick={analyze}
                  >
                    {detecting ? t('roadDamages.analyzing') : t('roadDamages.analyze')}
                  </button>
                </div>
              </>
            )}

            {step === 'review' && (
              <>
                <div className="road-preview road-detection">
                  <img src={imageDataUrl} alt={t('roadDamages.detectionAlt')} />
                  {detection && (
                    <div
                      className="road-box"
                      style={{
                        left: `${detection.box.x * 100}%`,
                        top: `${detection.box.y * 100}%`,
                        width: `${detection.box.w * 100}%`,
                        height: `${detection.box.h * 100}%`,
                      }}
                    >
                      <span>
                        {trCatalog(detection.damageName)} · {Math.round(detection.confidence * 100)}%
                      </span>
                    </div>
                  )}
                </div>
                {detection ? (
                  <p className="road-note">
                    {t('roadDamages.proposal', {
                      damage: trCatalog(detection.damageName),
                      confidence: Math.round(detection.confidence * 100),
                    })}
                  </p>
                ) : (
                  <p className="road-note">{t('roadDamages.notDetected')}</p>
                )}
                <div className="road-actions">
                  {detection ? (
                    <>
                      <button type="button" className="road-cancel" onClick={rejectDetection}>
                        {t('roadDamages.incorrectManual')}
                      </button>
                      <button type="button" className="attach-damage-btn" onClick={acceptDetection}>
                        {t('roadDamages.ok')}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="attach-damage-btn" onClick={rejectDetection}>
                      {t('roadDamages.enterManual')}
                    </button>
                  )}
                </div>
              </>
            )}

            {step === 'form' && imageDataUrl && (
              <div className="road-preview">
                <img src={imageDataUrl} alt={t('preview')} />
                <span>{imageName}</span>
              </div>
            )}

            {step === 'form' && (
            <>
            <label className="road-field">
              {t('roadDamages.damageType')}
              <select
                value={groupChoice}
                disabled={saving || lockedDamage}
                onChange={(event) => onGroupChange(event.target.value)}
              >
                <option value="">{t('roadDamages.select')}</option>
                {GROUPED.filter((group) => DAMAGE_GROUPS.includes(group)).map((group) => (
                  <option key={group} value={`group:${group}`}>
                    {trCatalog(group)}
                  </option>
                ))}
                {singles.map((damage) => (
                  <option key={damage.id} value={`damage:${damage.id}`}>
                    {trCatalog(damage.name)}
                  </option>
                ))}
              </select>
            </label>

            {groupChoice.startsWith('group:') && (
              <label className="road-field">
                {t('roadDamages.specificDamage')}
                <select
                  value={damageId}
                  disabled={saving || lockedDamage}
                  onChange={(event) => {
                    setDamageId(event.target.value);
                    setLocationId('');
                    setElementFamily('');
                    setDimension('');
                  }}
                >
                  <option value="">{t('roadDamages.select')}</option>
                  {SELECTABLE_DAMAGES.filter(
                    (damage) => damage.group === groupChoice.slice(6)
                  ).map((damage) => (
                    <option key={damage.id} value={damage.id}>
                      {trCatalog(damage.name).split('. ').slice(1).join('. ') || trCatalog(damage.name)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {selected?.unit && (
              <label className="road-field">
                {t('roadDamages.dimension')} ({selected.unit})
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={dimension}
                  disabled={saving}
                  onChange={(event) => setDimension(event.target.value)}
                  placeholder={t('dimensionValue', { unit: selected.unit })}
                />
              </label>
            )}

            {selected && (
              <>
                {elementFamilies.length > 1 && (
                  <label className="road-field">
                    {t('roadDamages.elementFamily')}
                    <select
                      value={activeFamily}
                      disabled={saving}
                      onChange={(event) => {
                        setElementFamily(event.target.value);
                        setLocationId('');
                      }}
                    >
                      <option value="">{t('roadDamages.select')}</option>
                      {elementFamilies.map((family) => (
                        <option key={family} value={family}>
                          {trCatalog(family)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {activeFamily && (
                  <label className="road-field">
                    {t('roadDamages.element')}
                    <select
                      value={locationId}
                      disabled={saving}
                      onChange={(event) => setLocationId(event.target.value)}
                    >
                      <option value="">{t('roadDamages.select')}</option>
                      {familyLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {trCatalog(location.label)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}

            <label className="road-field">
              {t('roadDamages.intensity')}
              <select
                value={intensity}
                disabled={saving}
                onChange={(event) =>
                  setIntensity(event.target.value ? Number(event.target.value) : '')
                }
              >
                <option value="">{t('roadDamages.select')}</option>
                {[1, 2, 3, 4].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label className="road-field">
              {t('roadDamages.optionalComment')}
              <textarea
                rows={3}
                value={comment}
                disabled={saving}
                onChange={(event) => setComment(event.target.value)}
                placeholder={t('roadDamages.commentPlaceholder')}
              />
            </label>

            <label className="road-field">
              {t('roadDamages.optionalVideo')}
              <RoadDamageVideo
                value={videoDataUrl}
                disabled={saving}
                onChange={setVideoDataUrl}
              />
            </label>

            <div className="road-result">
              <span>{t('roadDamages.severity')}</span>
              <strong className={severity ? `sev sev-${severity}` : ''}>
                {severity || '—'}
              </strong>
            </div>
            <p className="road-note">{t('roadDamages.registered')}: {formatDate(openedAt)}</p>
            {error && <p className="road-error">{error}</p>}

            <div className="road-actions">
              <button
                type="button"
                className="road-cancel"
                disabled={saving}
                onClick={() => setOpen(false)}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                className="attach-damage-btn"
                disabled={saving}
                onClick={submit}
              >
                {saving ? t('roadDamages.saving') : t('roadDamages.save')}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}
      {playingVideo && (
        <div className="road-modal-backdrop" onClick={closeVideo}>
          <div className="road-modal" onClick={(event) => event.stopPropagation()}>
            <div className="road-modal-head">
              <h3>{t('roadDamages.playVideo')}</h3>
              <button type="button" className="road-close" onClick={closeVideo}>
                ×
              </button>
            </div>
            <video
              className="road-video-player"
              src={playingVideo.url}
              controls
              autoPlay
              playsInline
              preload="auto"
              onError={() => setError(t('roadDamages.videoError'))}
            />
          </div>
        </div>
      )}
      {exportOpen && (
        <div className="road-modal-backdrop" onClick={() => setExportOpen(false)}>
          <div className="road-modal road-export-modal" onClick={(event) => event.stopPropagation()}>
            <div className="road-modal-head">
              <h3>{t('roadDamages.chooseFormat')}</h3>
              <button type="button" className="road-close" onClick={() => setExportOpen(false)}>
                ×
              </button>
            </div>
            <div className="road-export-options">
              <button type="button" className="road-download-btn" onClick={() => downloadList('pdf')}>
                <DownloadIcon fontSize="small" />
                {t('roadDamages.downloadPdfOption')}
              </button>
              <button type="button" className="road-download-btn" onClick={() => downloadList('xlsx')}>
                <DownloadIcon fontSize="small" />
                {t('roadDamages.downloadExcelOption')}
              </button>
            </div>
          </div>
        </div>
      )}

      {remedyRecord && (
        <div className="road-modal-backdrop" onClick={closeRemedy}>
          <div className="road-modal" onClick={(event) => event.stopPropagation()}>
            <div className="road-modal-head">
              <h3>
                {t(remedyMode === 'suggest' ? 'roadDamages.suggestTitle' : 'roadDamages.remedyTitle')} · ID:
                {String(remedyRecord.roadDamageNumber).padStart(4, '0')}
              </h3>
              <button type="button" className="road-close" disabled={remedying} onClick={closeRemedy}>
                ×
              </button>
            </div>
            {!remedyCamera && !remedyImage && (
              <div className="road-remedy-choices">
                <button type="button" className="road-download-btn" onClick={() => setRemedyCamera(true)}>
                  <PhotoCameraOutlinedIcon fontSize="small" />
                  {t('roadDamages.takePhoto')}
                </button>
                <label className="road-download-btn road-file-label">
                  <PhotoLibraryOutlinedIcon fontSize="small" />
                  {t('roadDamages.fromGallery')}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (!file) return;
                      fileToJpeg(file)
                        .then(setRemedyImage)
                        .catch(() => setError(t('roadDamages.remedyError')));
                    }}
                  />
                </label>
              </div>
            )}
            {remedyCamera && !remedyImage && (
              <RoadDamageCamera
                onCapture={(image) => {
                  setRemedyImage(image);
                  setRemedyCamera(false);
                }}
                onReset={() => setRemedyImage('')}
              />
            )}
            {remedyImage && (
              <img className="road-remedy-preview" src={remedyImage} alt={t('roadDamages.remedyImage')} />
            )}
            {error && <p className="road-error">{error}</p>}
            <div className="road-actions">
              <button type="button" className="road-cancel" disabled={remedying} onClick={closeRemedy}>
                {t('cancel')}
              </button>
              {remedyImage && (
                <button
                  type="button"
                  className="road-cancel"
                  disabled={remedying}
                  onClick={() => {
                    setRemedyImage('');
                    setRemedyCamera(false);
                  }}
                >
                  {t('roadDamages.retakePhoto')}
                </button>
              )}
              <button
                type="button"
                className="attach-damage-btn"
                disabled={remedying || !remedyImage}
                onClick={submitRemedy}
              >
                {remedying
                  ? t('roadDamages.saving')
                  : t(remedyMode === 'suggest' ? 'roadDamages.confirmSuggestion' : 'roadDamages.confirmRemedy')}
              </button>
            </div>
          </div>
        </div>
      )}

      {reviewRecord && (
        <div className="road-modal-backdrop" onClick={() => !reviewing && setReviewRecord(null)}>
          <div className="road-modal" onClick={(event) => event.stopPropagation()}>
            <div className="road-modal-head">
              <h3>
                {t('roadDamages.suggestionPending')} · ID:
                {String(reviewRecord.roadDamageNumber).padStart(4, '0')}
              </h3>
              <button
                type="button"
                className="road-close"
                disabled={reviewing}
                onClick={() => setReviewRecord(null)}
              >
                ×
              </button>
            </div>
            <p>
              {t('roadDamages.suggestedBy')}: {reviewRecord.roadRemedySuggestedBy || '—'}
              {reviewRecord.roadRemedySuggestionAt
                ? ` · ${formatDate(reviewRecord.roadRemedySuggestionAt)}`
                : ''}
            </p>
            {reviewRecord.roadRemedySuggestionImage && (
              <img
                className="road-remedy-preview"
                src={reviewRecord.roadRemedySuggestionImage}
                alt={t('roadDamages.remedyImage')}
              />
            )}
            {error && <p className="road-error">{error}</p>}
            <div className="road-actions">
              <button
                type="button"
                className="road-cancel"
                disabled={reviewing}
                onClick={() => reviewSuggestion('reject')}
              >
                {t('roadDamages.rejectSuggestion')}
              </button>
              <button
                type="button"
                className="attach-damage-btn"
                disabled={reviewing}
                onClick={() => reviewSuggestion('accept')}
              >
                {reviewing ? t('roadDamages.saving') : t('roadDamages.acceptSuggestion')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteReview && (
        <div className="road-modal-backdrop" onClick={() => !reviewing && setDeleteReview(null)}>
          <div className="road-modal" onClick={(event) => event.stopPropagation()}>
            <div className="road-modal-head">
              <h3>
                {t('roadDamages.deleteRequestPending')} · ID:
                {String(deleteReview.roadDamageNumber).padStart(4, '0')}
              </h3>
              <button
                type="button"
                className="road-close"
                disabled={reviewing}
                onClick={() => setDeleteReview(null)}
              >
                ×
              </button>
            </div>
            <p>
              {t('roadDamages.requestedBy')}: {deleteReview.roadDeleteRequestedBy || '—'}
              {deleteReview.roadDeleteRequestedAt
                ? ` · ${formatDate(deleteReview.roadDeleteRequestedAt)}`
                : ''}
            </p>
            {error && <p className="road-error">{error}</p>}
            <div className="road-actions">
              <button
                type="button"
                className="road-cancel"
                disabled={reviewing}
                onClick={() => reviewDeletion('reject')}
              >
                {t('roadDamages.rejectSuggestion')}
              </button>
              <button
                type="button"
                className="road-delete-btn"
                disabled={reviewing}
                onClick={() => reviewDeletion('accept')}
              >
                {reviewing ? t('roadDamages.saving') : t('roadDamages.acceptDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMap && <RoadDamagesMap damages={records} onClose={() => setShowMap(false)} />}
    </div>
  );
};
